import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../../context/AuthContext';
import { useSSO } from '../../hooks/useSSO';
import { LoginCredentials } from '../../types/auth';
import ChangeExpiredPassword from './ChangeExpiredPassword';

interface LocationState {
  from?: Location;
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const { loginWithSSO, isLoading: ssoLoading, error: ssoError } = useSSO();
  
  useEffect(() => {
    if (isAuthenticated) {
      const state = location.state as LocationState;
      navigate(state?.from?.pathname || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, location]);
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
    twoFactorToken: undefined,
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showExpiredPasswordForm, setShowExpiredPasswordForm] = useState(false);
  const [expiredPasswordUserId, setExpiredPasswordUserId] = useState<number | null>(null);
  const [showTwoFactorSetupDialog, setShowTwoFactorSetupDialog] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<number | null>(null);
  const [showTwoFactorInput, setShowTwoFactorInput] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleExpiredPasswordChangeSuccess = () => {
    // Reset state and go back to normal login
    setShowExpiredPasswordForm(false);
    setExpiredPasswordUserId(null);
    setShowTwoFactorInput(false);
    setError('Password changed successfully. Please login with your new password.');
    setFormData({ email: '', password: '', twoFactorToken: '' });
  };

  const handleBackToLogin = () => {
    setShowExpiredPasswordForm(false);
    setExpiredPasswordUserId(null);
    setShowTwoFactorInput(false);
    setError('');
  };

  const handleTwoFactorSetupDialogClose = () => {
    setShowTwoFactorSetupDialog(false);
    setPendingUserId(null);
    setIsLoading(false);
  };

  const handleSetupTwoFactor = async () => {
    setShowTwoFactorSetupDialog(false);
    // Navigate to the dedicated 2FA setup page
    const state = location.state as LocationState;
    navigate('/setup-2fa', { 
      state: { 
        from: state?.from?.pathname || '/',
        userId: pendingUserId 
      },
      replace: true 
    });
  };

  const handleSkipTwoFactor = async () => {
    setShowTwoFactorSetupDialog(false);
    setIsLoading(true);
    
    try {
      // User chose to skip 2FA setup
      const skipResult = await login({
        ...formData,
        skipTwoFactorSetup: true
      } as any);
      
      // Handle the result of skipping 2FA setup
      if ('user' in skipResult) {
        const { user } = skipResult;
        if (user.companies.length === 0) {
          setError('You don\'t have access to any companies. Please contact your administrator.');
          setIsLoading(false);
          return;
        }
        const state = location.state as LocationState;
        navigate(state?.from?.pathname || '/', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      // SECURITY FIX: Client-side validation to prevent empty passwords
      if (!formData.email || formData.email.trim().length === 0) {
        setError('Email is required and cannot be empty');
        setIsLoading(false);
        return;
      }
      
      if (!formData.password || formData.password.trim().length === 0) {
        setError('Password is required and cannot be empty');
        setIsLoading(false);
        return;
      }
      
      // Additional validation
      if (formData.password.length < 1) {
        setError('Password cannot be empty');
        setIsLoading(false);
        return;
      }
      
      const result = await login(formData);
      
      // Debug: Log the result to see what we're getting
      console.log('Login result:', result);
      
      // Check if password is expired
      if ('passwordExpired' in result && result.passwordExpired) {
        setShowExpiredPasswordForm(true);
        setExpiredPasswordUserId(result.userId);
        setError(result.message);
        setIsLoading(false); // Make sure to clear loading state
        return;
      }
      
      // Check if 2FA is required
      if ('requiresTwoFactor' in result && result.requiresTwoFactor) {
        setShowTwoFactorInput(true);
        setError('');
        setIsLoading(false);
        return;
      }
      
      // Check if 2FA setup is suggested (user is now authenticated)
      console.log('Checking 2FA suggestion...', result);
      if ('user' in result && 'suggestTwoFactorSetup' in result && result.suggestTwoFactorSetup) {
        // User is authenticated, show 2FA setup suggestion
        const loginResult = result as { user: any; token: string | null; suggestTwoFactorSetup: boolean };
        const { user } = loginResult;
        
        console.log('2FA setup suggestion detected, setting sessionStorage');
        
        // Complete the login process first
        if (user.companies.length === 0) {
          setError('You don\'t have access to any companies. Please contact your administrator.');
          setIsLoading(false);
          return;
        }
        
        // Store that we should suggest 2FA setup after login completes
        sessionStorage.setItem('suggestTwoFactorSetup', 'true');
        
        // Navigate to dashboard - the Layout component can show the 2FA setup suggestion
        const state = location.state as LocationState;
        navigate(state?.from?.pathname || '/', { replace: true });
        setIsLoading(false);
        return;
      }
      
      // Normal login flow - result is guaranteed to have user property here
      const loginResult = result as { user: any; token: string | null };
      const { user } = loginResult;
      
      // Handle company selection based on number of companies
      if (user.companies.length === 0) {
        setError('You don\'t have access to any companies. Please contact your administrator.');
        return;
      }
      
      // Navigation will be handled by CompanySelectionWrapper based on company count
      const state = location.state as LocationState;
      navigate(state?.from?.pathname || '/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {showExpiredPasswordForm ? (
        <ChangeExpiredPassword
          userId={expiredPasswordUserId!}
          message={error}
          onSuccess={handleExpiredPasswordChangeSuccess}
          onCancel={handleBackToLogin}
        />
      ) : (
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.default',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: '100%',
              maxWidth: 400,
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" component="h1" gutterBottom>
              SaaS iCom Service
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              {showTwoFactorInput ? 'Enter your two-factor authentication code' : 'Sign in to your account'}
            </Typography>

            {(error || ssoError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || ssoError}
              </Alert>
            )}
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                error={!!error}
                autoComplete="email"
                disabled={showTwoFactorInput}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                error={!!error}
                autoComplete="current-password"
                disabled={showTwoFactorInput}
              />
              {showTwoFactorInput && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Two-Factor Authentication Code"
                  name="twoFactorToken"
                  type="text"
                  value={formData.twoFactorToken}
                  onChange={handleChange}
                  required
                  error={!!error}
                  placeholder="Enter 6-digit code from your authenticator app"
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                  }}
                  helperText="Enter the 6-digit code from your authenticator app or backup code"
                  autoFocus
                />
              )}
              {showTwoFactorInput && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={handleBackToLogin}
                  sx={{ mt: 2 }}
                >
                  Back to Login
                </Button>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ mt: showTwoFactorInput ? 1 : 3 }}
              >
                {isLoading ? 'Signing in...' : showTwoFactorInput ? 'Verify & Sign In' : 'Sign In'}
              </Button>
            </form>

            {!showTwoFactorInput && (
              <>
                <Divider sx={{ my: 3 }}>or</Divider>

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  disabled={ssoLoading || isLoading}
                  onClick={loginWithSSO}
                  startIcon={<BusinessIcon />}
                  sx={{ 
                    mt: 1,
                    borderColor: '#0078d4',
                    color: '#0078d4',
                    '&:hover': {
                      borderColor: '#106ebe',
                      backgroundColor: 'rgba(16, 110, 190, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#c8c6c4',
                      color: '#a19f9d',
                    }
                  }}
                >
                  {ssoLoading ? 'Connecting...' : 'Login with Aptean SSO'}
                </Button>
              </>
            )}
          </Paper>
        </Box>
      )}
      
      {/* Two-Factor Authentication Setup Dialog */}
      <Dialog
        open={showTwoFactorSetupDialog}
        onClose={handleTwoFactorSetupDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1
          }}
        >
          <SecurityIcon color="primary" />
          <Typography variant="h6" component="span">
            Enhanced Security Recommended
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Two-factor authentication (2FA) adds an extra layer of security to your account. 
            It's highly recommended to protect your data and ensure only you can access your account.
          </DialogContentText>
          
          <Box sx={{ 
            bgcolor: 'info.main', 
            color: 'info.contrastText',
            p: 2, 
            borderRadius: 1,
            mb: 2
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              🔒 Why use 2FA?
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              • Protects against password theft<br />
              • Prevents unauthorized access<br />
              • Meets security best practices
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            You can always set this up later from your profile settings.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleSkipTwoFactor}
            color="inherit"
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Continue Without 2FA
          </Button>
          <Button
            onClick={handleSetupTwoFactor}
            color="primary"
            variant="contained"
            startIcon={<SecurityIcon />}
          >
            Set Up 2FA Now
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Login;
